// built-from: /engagement-brief-surface v1 impl:engagement-brief-surface role:implementation
// built-from: /objectives-surface v1 impl:objectives-surface role:implementation
// built-from: /domains-stakeholders-surface v1 impl:domains-stakeholders-surface role:implementation
// built-from: /engagement-outline-hydration v1 impl:engagement-outline-hydration role:implementation
// built-from: /engagement-context-panel v1 impl:engagement-context-panel role:implementation
// Feature 075: the brief-first screens. Every screen is the same thing — a
// capture surface backed by a public JSON structure, beside a facilitator that
// IS the existing chat (attachFacilitator + ContextualFacilitatorCompanion,
// instantiated exactly as the other facilitated screens do — no chat changes).
// Layout: capture surface on the LEFT, chat on the RIGHT (fshell--chat-right).

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { attachFacilitator } from '../facilitation/ContextualFacilitatorCompanion';
import { ActivityShell } from '../facilitation/ActivityShell';
import {
  ActivityAttachment,
  ActivityConversationPanel,
  useActivityController,
  type ActivityFacilitationBinding,
} from '../facilitation/ActivityConversation';
import { useEngagement } from '../hooks/useEngagement';
import { useActivityFacilitation } from '../hooks/useActivityFacilitation';
import { ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID } from '../facilitation/activityPlanCapability';
import type { EngagementActivity, EngagementSetup } from '../hooks/useEngagementSetup';
import type { EngagementSetupModel } from './EngagementSetupWorkspace';
import { engagementSpecialistFor, normaliseEngagementSpecialistId } from './engagementSpecialists';
import {
  ActivityNotesSurface,
  BriefOutcomeSurface,
  BusinessMapSurface,
  ObjectivesOutcomeSurface,
  type ActivityNotesDocument,
  type BriefDocument,
  type DomainsDocument,
  type ObjectivesDocument,
} from './OutlineSurfaces';
import '../styles/BriefFirstWorkspace.css';

// 077: the Brief's own capability ids. Capture capabilities come from the
// activity contract pool projection, never a client hardcode.
const BRIEF_OWN_CAPABILITY_IDS = Object.freeze([
  'engagement-brief.set_narrative',
  'engagement-brief.set_goal',
  'engagement-brief.set_engagement_type',
  'engagement-brief.pull_in_activity',
  'engagement-brief.remove_activity',
  'engagement-brief.close_session',
]);

interface ActivityPoolMember {
  key: string;
  name: string;
  expectedOutput: string;
  documentKey: string;
  // Every capability id the Brief chat may run for this member: captures
  // plus the reconciliation revision (remove) tools (079 addendum).
  chatCapabilityIds: string[];
}

// The read-only projection of the server's activity contract pool (077):
// drives the plan's add-list and the capture-capability allowlist.
function useActivityPool(): ActivityPoolMember[] {
  const [pool, setPool] = useState<ActivityPoolMember[]>([]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch('/api/facilitation/activity-pool');
        if (!response.ok) return;
        const body = await response.json() as { activities?: ActivityPoolMember[] };
        if (!cancelled && Array.isArray(body.activities)) setPool(body.activities);
      } catch {
        // Silent: the add-list falls back to the surface's default options.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return pool;
}

// Each pool member's live document, for the quiet per-card gap hints. One
// state bag (the pool's length is dynamic, so per-member hooks cannot be used).
function useActivityDocuments(engagementId: string | null, pool: ActivityPoolMember[]) {
  const [docs, setDocs] = useState<Record<string, unknown>>({});
  const refresh = useCallback(async () => {
    if (!engagementId || pool.length === 0) return;
    const entries = await Promise.all(pool.map(async (member) => {
      try {
        const response = await fetch(
          `/api/engagements/${encodeURIComponent(engagementId)}/${member.documentKey}`,
        );
        return [member.key, response.ok ? await response.json() : undefined] as const;
      } catch {
        return [member.key, undefined] as const;
      }
    }));
    setDocs(Object.fromEntries(entries.filter(([, doc]) => doc !== undefined)));
  }, [engagementId, pool]);
  useEffect(() => { void refresh(); }, [refresh]);
  return { docs, refresh };
}

// Generic emptiness check: an activity document is "still empty" while every
// array it holds is empty — works for any pool member's shape.
function documentEntryCount(doc: unknown): number {
  if (!doc || typeof doc !== 'object') return 0;
  return Object.values(doc as Record<string, unknown>)
    .reduce<number>((count, value) => count + (Array.isArray(value) ? value.length : 0), 0);
}

const OBJECTIVES_CAPABILITY_IDS = Object.freeze([
  ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID,
  'engagement-objectives.set_diagnosis',
  'engagement-objectives.set_guiding_policy',
  'engagement-objectives.capture_objective',
  'engagement-objectives.remove_objective',
  'engagement-objectives.close_session',
]);

const DOMAINS_CAPABILITY_IDS = Object.freeze([
  ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID,
  'engagement-domains.capture_domain',
  'engagement-domains.capture_process',
  'engagement-domains.rank_processes',
  'engagement-domains.remove_process',
  'engagement-domains.capture_stakeholder',
  'engagement-domains.remove_domain',
  'engagement-domains.remove_stakeholder',
  'engagement-domains.close_session',
]);

// A public engagement sub-document, refreshed whenever the conversation lands
// new facts (the server folds facts into the document before the turn returns).
export function useEngagementDoc<T>(engagementId: string | null, sub: string) {
  const [doc, setDoc] = useState<T | undefined>(undefined);
  const refresh = useCallback(async () => {
    if (!engagementId) return;
    try {
      const response = await fetch(`/api/engagements/${encodeURIComponent(engagementId)}/${sub}`);
      if (!response.ok) return;
      setDoc(await response.json() as T);
    } catch {
      // Silent: the next fact turn or navigation refreshes.
    }
  }, [engagementId, sub]);
  useEffect(() => { void refresh(); }, [refresh]);
  return { doc, refresh };
}

// ── Process register + process documents (089) ──────────────────────
// "One process, one document": the register is the switcher's list
// (name, order, state, step count, live/removed); each process's own
// document is the ONLY render source for its step layer, walk band,
// canvas and source modal. Neither the register nor a process document
// is ever hand-edited by the client — every mutation goes through a
// deterministic route and the server's validation gate.

export interface ProcessRegisterEntry {
  processId: string;
  name: string;
  order: number;
  state: 'empty' | 'narrated' | 'walked' | 'agreed';
  source?: 'brief' | 'narration' | 'consultant' | 'domains';
  stepCount: number;
  updatedAt?: string;
  removedAt: string | null;
  // 095: the business map's spine — which domain a process belongs to,
  // its importance, its storm rank and its one-line why.
  domainId: string | null;
  importance: 'core' | 'supporting' | 'generic' | null;
  rank: number | null;
  why: string;
}

export interface ProcessRegisterDoc {
  processes: ProcessRegisterEntry[];
}

// The engagement's process register (089): every process's name, order,
// state and step count, live and removed. `createProcess` posts by name
// alone — no model turn — and hands back the new processId so the caller
// can focus it immediately. Rename/remove/restore/reorder are
// deterministic per-process calls; the register refreshes after each.
export function useProcessRegister(engagementId: string | null) {
  const [register, setRegister] = useState<ProcessRegisterDoc>({ processes: [] });
  const [glossary, setGlossary] = useState<unknown>(undefined);
  const refresh = useCallback(async () => {
    if (!engagementId) return;
    try {
      const response = await fetch(`/api/engagements/${encodeURIComponent(engagementId)}/processes`);
      if (!response.ok) return;
      const body = await response.json() as { register?: ProcessRegisterDoc; glossary?: unknown };
      if (body.register) setRegister(body.register);
      if (body.glossary !== undefined) setGlossary(body.glossary);
    } catch {
      // Silent: the next navigation or action refreshes.
    }
  }, [engagementId]);
  useEffect(() => { void refresh(); }, [refresh]);

  const post = useCallback(async (path: string, body?: Record<string, unknown>) => {
    if (!engagementId) return undefined;
    try {
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(engagementId)}/processes${path}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) },
      );
      if (!response.ok) return undefined;
      const data = await response.json().catch(() => ({} as unknown));
      await refresh();
      return data;
    } catch {
      return undefined;
    }
  }, [engagementId, refresh]);

  const createProcess = useCallback(
    (name: string) => post('', { name }) as Promise<{ processId: string } | undefined>,
    [post],
  );
  const renameProcess = useCallback(
    (processId: string, name: string) => post(`/${encodeURIComponent(processId)}/rename`, { name }),
    [post],
  );
  const removeProcess = useCallback(
    (processId: string) => post(`/${encodeURIComponent(processId)}/remove`),
    [post],
  );
  const restoreProcess = useCallback(
    (processId: string) => post(`/${encodeURIComponent(processId)}/restore`),
    [post],
  );
  const reorderProcess = useCallback(
    (processId: string, direction: 'up' | 'down') => post(`/${encodeURIComponent(processId)}/reorder`, { direction }),
    [post],
  );

  return { register, glossary, refresh, createProcess, renameProcess, removeProcess, restoreProcess, reorderProcess };
}

export interface ProcessDocumentRejected {
  at: string;
  reason: string;
  raw: unknown;
  errors?: Array<{ line: number | null; id: number | null; message: string }>;
}

export interface ProcessDocument {
  version?: number;
  processId?: string;
  name?: string;
  flowText: string;
  domainFlowText: string;
  domainAssumptions: Array<{ part: number; partLabel: string; lines: string[] }>;
  rejected: ProcessDocumentRejected | null;
  updatedAt?: string;
  stepIdentity?: { byLocalId: Record<string, string>; allocated: string[] };
}

// One process's document (089): the ONLY render source for the focused
// process's step layer, walk band, canvas and source modal. Undo and
// checkpoint are deterministic server calls through the validation gate
// (no model turn); the refresh after each brings map, band and canvas
// forward together.
export function useProcessDocument(engagementId: string | null, processId: string | null) {
  const [doc, setDoc] = useState<ProcessDocument | undefined>(undefined);
  const [revision, setRevision] = useState<string | undefined>(undefined);
  const refresh = useCallback(async () => {
    if (!engagementId || !processId) return;
    try {
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(engagementId)}/processes/${encodeURIComponent(processId)}`,
      );
      if (!response.ok) return;
      const body = await response.json() as { doc?: ProcessDocument; revision?: string };
      if (body.doc) setDoc(body.doc);
      setRevision(body.revision);
    } catch {
      // Silent: the next turn or navigation refreshes.
    }
  }, [engagementId, processId]);
  useEffect(() => {
    setDoc(undefined);
    setRevision(undefined);
    void refresh();
  }, [refresh]);

  const post = useCallback(async (action: string, body?: Record<string, unknown>) => {
    if (!engagementId || !processId) return false;
    try {
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(engagementId)}/processes/${encodeURIComponent(processId)}/${action}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) },
      );
      if (response.ok) {
        setLastError(null);
        await refresh();
        return true;
      }
      setLastError(await describeProcessFailure(response, action));
      // A revision conflict means the screen is behind: bring it forward
      // so the next attempt carries the live revision.
      if (response.status === 409) await refresh();
      return false;
    } catch {
      setLastError('That change did not reach the server. Check the connection and try again.');
      return false;
    }
  }, [engagementId, processId, refresh]);
  const [lastError, setLastError] = useState<string | null>(null);

  // Undo and checkpoint restore are human edits: they carry the revision
  // the screen last read, so a change landed in between is refused (409)
  // rather than silently overwritten.
  const undo = useCallback(() => post('undo', { expectedRevision: revision }), [post, revision]);
  const takeCheckpoint = useCallback((label: string) => post('checkpoint', { label }), [post]);
  const clearError = useCallback(() => setLastError(null), []);

  // 091: move a part (a top-level step and its subtree) to another live
  // process, or to a new one named after it. Carries the revision the
  // screen last read; the target's id comes back so the caller can
  // refocus when a process was created.
  const movePart = useCallback(async (
    partId: number,
    destination: { toProcessId: string } | { newProcessName: string },
  ): Promise<{ targetProcessId: string; created: boolean } | null> => {
    if (!engagementId || !processId) return null;
    try {
      const response = await fetch(
        `/api/engagements/${encodeURIComponent(engagementId)}/processes/${encodeURIComponent(processId)}/move-part`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ partId, expectedRevision: revision, ...destination }),
        },
      );
      if (!response.ok) {
        setLastError(await describeProcessFailure(response, 'move'));
        if (response.status === 409) await refresh();
        return null;
      }
      const body = await response.json() as { targetProcessId: string; created?: boolean };
      setLastError(null);
      await refresh();
      return { targetProcessId: body.targetProcessId, created: body.created === true };
    } catch {
      setLastError('That change did not reach the server. Check the connection and try again.');
      return null;
    }
  }, [engagementId, processId, revision, refresh]);

  return { doc, revision, refresh, undo, takeCheckpoint, movePart, lastError, clearError };
}

// Plain-language reasons for a refused process write, from the route's
// status and reason code (the route answers with codes; the screen speaks).
async function describeProcessFailure(response: Response, action: string): Promise<string> {
  let reason = '';
  try {
    const body = await response.json() as { error?: string };
    reason = typeof body.error === 'string' ? body.error : '';
  } catch {
    reason = '';
  }
  if (reason === 'checkpoints-all-agreed') {
    return 'This process already holds ten agreed checkpoints. Unmark one before taking another.';
  }
  if (reason === 'no-previous-state') {
    return 'There is nothing to undo yet.';
  }
  if (reason === 'part-not-found') {
    return 'That part is no longer in this process. The screen has been reloaded.';
  }
  if (response.status === 409) {
    return 'This process changed since the screen last read it. It has been reloaded; try again.';
  }
  if (response.status === 422) {
    return 'That change breaks the notation and was not saved.';
  }
  return `The ${action.replace(/-/g, ' ')} did not save (${response.status}).`;
}

// Re-read documents after every landed fact turn: the controller's fact log is
// the change signal, the documents are the render source. `refresh` is read
// through a ref so the effect's dependencies are the actual change signal
// (factCount, lastTurn) only — callers pass a fresh function every render,
// and depending on it directly re-fires this on every render, not just when
// facts land.
function useFactDrivenRefresh(refresh: () => void) {
  // Works before the session exists too: the surface then renders from its own
  // document with nothing captured in this conversation yet (decision 10).
  const controller = useActivityController();
  const factCount = controller.state.facts.length + controller.state.drafts.length;
  const lastTurn = controller.state.lastTurnFactIds?.join('|') || '';
  const first = useRef(true);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    refreshRef.current();
  }, [factCount, lastTurn]);
  return controller;
}

// 100 (owner decisions 9 and 10): the private chrome these screens used to
// build — their own header, their own companionOpen state, their own
// "Open facilitator" button and an inline context panel — is gone. Every
// brief-first screen renders through the ONE ActivityShell, with the ONE
// conversation beside it from the moment it opens.
function outlineConversation(facilitation: ActivityFacilitationBinding) {
  return {
    render: ({ close }: { close: () => void }) => (
      <ActivityConversationPanel facilitation={facilitation} onClose={close} />
    ),
    opening: facilitation.opening,
    error: facilitation.error || null,
    onRetry: facilitation.open,
  };
}

// ── The Brief ──────────────────────────────────────────────────────

function BriefScreenContent({ model, engagementName, brief, refreshBrief, pool, activityDocs, refreshActivityDocs, facilitation }: {
  model: EngagementSetupModel;
  engagementName: string;
  brief: BriefDocument | undefined;
  refreshBrief: () => void;
  pool: ActivityPoolMember[];
  activityDocs: Record<string, unknown>;
  refreshActivityDocs: () => void;
  facilitation: ActivityFacilitationBinding;
}) {
  const navigate = useNavigate();
  const refreshAll = useCallback(() => {
    refreshBrief();
    refreshActivityDocs();
    void model.refreshQuietly?.();
  }, [model, refreshActivityDocs, refreshBrief]);
  const controller = useFactDrivenRefresh(refreshAll);
  const setup = model.setup as EngagementSetup;
  // 077: a pulled-in activity whose document is still empty carries a quiet
  // hint on its card — the gap steers the story without a spoken question.
  const activityHints: Record<string, string> = {};
  for (const member of pool) {
    const doc = activityDocs[member.key];
    if (doc !== undefined && documentEntryCount(doc) === 0) {
      activityHints[member.key] = 'Nothing captured here yet. More story fills it.';
    }
  }
  return (
    <ActivityShell
      title="Brief"
      context={engagementName}
      saving={controller.pendingCount > 0 || model.status === 'saving'}
      sealed={controller.state.sealed}
      conversation={outlineConversation(facilitation)}
    >
      {controller.rejection ? <div role="alert" className="fshell-alert">{controller.rejection}</div> : null}
      {model.error ? <div role="alert" className="fshell-alert">{model.error.message}</div> : null}
      <BriefOutcomeSurface
        state={controller.state}
        busy={controller.pendingCount > 0}
        onEmitEdit={(edit) => controller.execute(edit).then((accepted) => {
          if (accepted) refreshAll();
          return accepted;
        })}
        brief={brief}
        activities={setup?.activities || []}
        activityOptions={pool.length > 0
          ? pool.map((member) => ({ key: member.key, label: member.name }))
          : undefined}
        activityHints={activityHints}
        plan={{
          onRemove: (activityKey) => {
            void controller.execute({
              capabilityId: 'engagement-brief.remove_activity',
              input: { activity: activityKey },
            }).then((accepted) => { if (accepted) refreshAll(); });
          },
          onAdd: (activityKey) => {
            void controller.execute({
              capabilityId: 'engagement-brief.pull_in_activity',
              input: { activity: activityKey, reason: 'Added by the consultant' },
            }).then((accepted) => { if (accepted) refreshAll(); });
          },
          onActivityField: (activityId, field, value) => {
            void model.change({
              type: 'editActivityField',
              target: { kind: 'activity', id: activityId, field },
              value,
            });
          },
          onOpen: (activity) => {
            const specialist = engagementSpecialistFor(activity.specialistMethod);
            navigate(specialist
              ? `/engagement/activities/${encodeURIComponent(activity.id)}/specialist/${specialist.routePart}`
              : `/engagement/activities/${encodeURIComponent(activity.id)}`);
          },
        }}
      />
    </ActivityShell>
  );
}

// 100, decision 6: the Brief is an activity. It keeps its own route and opens
// in the same shell, with the same conversation and states as every other
// activity — and, like them, it allocates no session until the first message
// (decision 10). It needs no plan record to get any of that.
export function EngagementBriefWorkspace({ model, engagementName }: {
  model: EngagementSetupModel;
  engagementName: string;
}) {
  const { currentEngagementId } = useEngagement();
  const { doc: brief, refresh: refreshBrief } = useEngagementDoc<BriefDocument>(currentEngagementId, 'brief');
  const pool = useActivityPool();
  const { docs: activityDocs, refresh: refreshActivityDocs } = useActivityDocuments(currentEngagementId, pool);
  const setup = model.setup;
  const facilitator = useActivityFacilitation({
    scopeKey: `${currentEngagementId}/brief`,
    engagementKey: currentEngagementId || '',
    ensureSession: () => (model.ensureBriefSession ? model.ensureBriefSession() : Promise.resolve(null)),
  });
  // An existing brief conversation is resumed as soon as the screen opens.
  const sessionId = facilitator.sessionId || setup?.briefFacilitationSessionId || '';
  const facilitation: ActivityFacilitationBinding = { ...facilitator, sessionId };
  const attachment = attachFacilitator({
    screenId: 'engagement-brief-surface',
    profileId: 'engagement-brief',
    resolveSession: () => sessionId || null,
    getContext: () => ({
      revision: 0,
      focus: null,
      values: {
        narrative: brief?.narrative || '',
        goal: brief?.goal || '',
        engagementType: brief?.engagementType || '',
      },
    }),
    allowedCapabilityIds: [
      ...BRIEF_OWN_CAPABILITY_IDS,
      ...pool.flatMap((member) => member.chatCapabilityIds),
    ],
    actions: {},
  });
  return (
    <ActivityAttachment facilitation={facilitation} attachment={attachment}>
      <BriefScreenContent
        model={model}
        engagementName={engagementName}
        brief={brief}
        refreshBrief={() => { void refreshBrief(); }}
        pool={pool}
        activityDocs={activityDocs}
        refreshActivityDocs={() => { void refreshActivityDocs(); }}
        facilitation={facilitation}
      />
    </ActivityAttachment>
  );
}

// ── Activity screens (Objectives, Domains & Stakeholders) ──────────

// One activity screen for every brief-first working surface. The shell owns
// the title, the context line, save feedback, the dock's states and the
// responsive layout; this supplies the working content and the actions.
export function OutlineActivityScreen({ activity, title, doc, refreshDoc, renderSurface, facilitation }: {
  activity: EngagementActivity;
  title: string;
  doc: unknown;
  refreshDoc: () => void;
  facilitation: ActivityFacilitationBinding;
  renderSurface: (args: {
    controller: ReturnType<typeof useActivityController>;
    onEmitEdit: (edit: { capabilityId: string; input: Record<string, unknown> }) => Promise<boolean>;
  }) => ReactNode;
}) {
  const controller = useFactDrivenRefresh(refreshDoc);
  const onEmitEdit = useCallback(async (edit: { capabilityId: string; input: Record<string, unknown> }) => {
    const accepted = await controller.execute(edit);
    if (accepted) refreshDoc();
    return accepted;
  }, [controller, refreshDoc]);
  return (
    <ActivityShell
      title={title}
      context={`${activity.name} · facilitated working session`}
      saving={controller.pendingCount > 0}
      sealed={controller.state.sealed}
      actions={(
        <>
          <Link to={`/engagement/activities/${encodeURIComponent(activity.id)}`}>Back to activity</Link>
          <Link to="/engagement/brief">Back to brief</Link>
        </>
      )}
      conversation={outlineConversation(facilitation)}
    >
      {controller.rejection ? <div role="alert" className="fshell-alert">{controller.rejection}</div> : null}
      {doc === undefined ? <div role="status">Loading...</div> : renderSurface({ controller, onEmitEdit })}
    </ActivityShell>
  );
}

export function useEnsuredActivitySession(
  model: EngagementSetupModel,
  activityId: string,
  specialistId: string,
) {
  const setup = model.setup as EngagementSetup;
  const activity = setup.activities.find((candidate) => candidate.id === activityId);
  const linked = setup.activitySessions.find((candidate) => (
    candidate.activityId === activityId
    && candidate.specialistId === normaliseEngagementSpecialistId(activity?.specialistMethod)
  ));
  const matches = Boolean(activity)
    && engagementSpecialistFor(activity?.specialistMethod)?.id === specialistId;
  const { currentEngagementId } = useEngagement();
  const facilitator = useActivityFacilitation({
    scopeKey: `${currentEngagementId}/${activityId}/${specialistId}`,
    engagementKey: currentEngagementId || '',
    ensureSession: () => model.ensureActivitySession(activityId),
  });
  // An activity that already has a conversation resumes it on arrival; one
  // that does not gets its session from the first message (decision 10).
  const facilitation: ActivityFacilitationBinding = {
    ...facilitator,
    sessionId: facilitator.sessionId || linked?.sessionId || '',
  };
  return { activity, matches, facilitation };
}

export function NavigateToActivity({ activityId }: { activityId: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/engagement/activities/${encodeURIComponent(activityId)}`, { replace: true });
  }, [activityId, navigate]);
  return <div className="eng-setup-loading" role="status">Opening activity...</div>;
}

// 100 slice 1: Objectives is the first activity to render through the shared
// activity shell. The shell owns the title, context line, save feedback,
// launcher, dock states and responsive layout; this screen supplies its
// working content, its actions and the conversation binding. The private
// companionOpen state and the hand-rolled header are gone.
export function EngagementObjectivesWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const { currentEngagementId } = useEngagement();
  const { doc, refresh } = useEngagementDoc<ObjectivesDocument>(currentEngagementId, 'objectives');
  const { activity, matches, facilitation } = useEnsuredActivitySession(model, activityId, 'objectives');
  if (!activity || !matches) return <NavigateToActivity activityId={activityId} />;
  const attachment = attachFacilitator({
    screenId: 'objectives-surface',
    profileId: 'engagement-objectives',
    resolveSession: () => facilitation.sessionId || null,
    getContext: () => ({ revision: 0, focus: null, values: {} }),
    allowedCapabilityIds: OBJECTIVES_CAPABILITY_IDS,
    actions: {},
  });
  return (
    <ActivityAttachment facilitation={facilitation} attachment={attachment}>
      <OutlineActivityScreen
        activity={activity}
        title="Objectives"
        doc={doc}
        refreshDoc={() => { void refresh(); }}
        facilitation={facilitation}
        renderSurface={({ controller, onEmitEdit }) => (
          <ObjectivesOutcomeSurface
            state={controller.state}
            busy={controller.pendingCount > 0}
            onEmitEdit={onEmitEdit}
            objectivesDoc={doc}
          />
        )}
      />
    </ActivityAttachment>
  );
}

// 082: ONE workspace for every notes-shaped catalogue activity. The activity's
// own specialist key parameterises the document, the capability ids, and the
// facilitation profile — nothing here names an activity.
export function EngagementActivityNotesWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const { currentEngagementId } = useEngagement();
  const setup = model.setup as EngagementSetup;
  const activity = setup.activities.find((candidate) => candidate.id === activityId);
  const key = normaliseEngagementSpecialistId(activity?.specialistMethod) || '';
  const { doc, refresh } = useEngagementDoc<ActivityNotesDocument>(currentEngagementId, key);
  const { matches, facilitation } = useEnsuredActivitySession(model, activityId, key);
  // 098 retains the earlier notes document as prior material after Cloud
  // Architecture Design gains its structured workspace.
  const isNotesActivity = engagementSpecialistFor(key)?.routePart === 'notes'
    || key === 'cloud-architecture-design';
  if (!activity || !matches || !isNotesActivity) return <NavigateToActivity activityId={activityId} />;
  if (key === 'cloud-architecture-design') {
    return (
      <div className="journey-specialist-workspace">
        <header className="journey-page__header">
          <span className="journey-eyebrow">Cloud Architecture Design</span>
          <h1>Earlier architecture notes</h1>
          <p>Prior material captured before the structured platform workspace was introduced.</p>
        </header>
        <div className="platform-architecture__top">
          <Link to={`/engagement/activities/${encodeURIComponent(activityId)}/specialist/platform-architecture`}>Back to platform architecture</Link>
        </div>
        {!doc ? <p role="status">Loading earlier notes...</p> : doc.notes.length === 0 ? <p>No earlier architecture notes were captured.</p> : (
          <ul className="outline-notes" aria-label="Earlier architecture notes">
            {doc.notes.map((note) => <li key={note.id} className="outline-plan-card"><strong>{note.title || 'Architecture note'}</strong><p>{note.detail}</p>{note.source ? <small>Source: {note.source}</small> : null}</li>)}
          </ul>
        )}
      </div>
    );
  }
  const attachment = attachFacilitator({
    screenId: 'activity-notes-surface',
    profileId: `activity-${key}`,
    resolveSession: () => facilitation.sessionId || null,
    getContext: () => ({ revision: 0, focus: null, values: {} }),
    allowedCapabilityIds: [
      ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID,
      `activity-${key}.capture_note`,
      `activity-${key}.remove_note`,
      `activity-${key}.close_session`,
    ],
    actions: {},
  });
  return (
    <ActivityAttachment facilitation={facilitation} attachment={attachment}>
      <OutlineActivityScreen
        activity={activity}
        title={activity.name}
        doc={doc}
        refreshDoc={() => { void refresh(); }}
        facilitation={facilitation}
        renderSurface={({ controller, onEmitEdit }) => (
          <ActivityNotesSurface
            state={controller.state}
            busy={controller.pendingCount > 0}
            onEmitEdit={onEmitEdit}
            activityKey={key}
            notesDoc={doc}
          />
        )}
      />
    </ActivityAttachment>
  );
}

// 095: the business map opens a process straight into Event Storming — the
// same route the plan already links to, with the process carried on the
// query string the way the switcher and the landscape do.
function useOpenProcessInEventStorming(activityId: string) {
  const navigate = useNavigate();
  return (processId: string) => {
    navigate(`/engagement/business-map?process=${encodeURIComponent(processId)}&method=steps&originActivity=${encodeURIComponent(activityId)}`);
  };
}

export function EngagementDomainsStakeholdersWorkspace({ model, activityId }: {
  model: EngagementSetupModel;
  activityId: string;
}) {
  const { currentEngagementId } = useEngagement();
  const { doc, refresh } = useEngagementDoc<DomainsDocument>(currentEngagementId, 'domains');
  const { register, refresh: refreshRegister } = useProcessRegister(currentEngagementId);
  const { activity, matches, facilitation } = useEnsuredActivitySession(model, activityId, 'domains-stakeholders');
  const openProcess = useOpenProcessInEventStorming(activityId);
  if (!activity || !matches) return <NavigateToActivity activityId={activityId} />;
  const attachment = attachFacilitator({
    screenId: 'domains-stakeholders-surface',
    profileId: 'engagement-domains',
    resolveSession: () => facilitation.sessionId || null,
    getContext: () => ({ revision: 0, focus: null, values: {} }),
    allowedCapabilityIds: DOMAINS_CAPABILITY_IDS,
    actions: {},
  });
  return (
    <ActivityAttachment facilitation={facilitation} attachment={attachment}>
      <OutlineActivityScreen
        activity={activity}
        title="Business map"
        doc={doc}
        refreshDoc={() => { void refresh(); void refreshRegister(); }}
        facilitation={facilitation}
        renderSurface={({ controller, onEmitEdit }) => (
          <BusinessMapSurface
            state={controller.state}
            busy={controller.pendingCount > 0}
            onEmitEdit={onEmitEdit}
            domainsDoc={doc}
            register={register}
            onOpenProcess={openProcess}
          />
        )}
      />
    </ActivityAttachment>
  );
}
